import { useState } from 'react';

import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';
import {
  ChannelBadge,
  ConversationStatusChip,
  IdleChip,
  formatDayTime,
  relativeTimeLabel,
} from '@/shared/ui/conversation';

import { useCloseConversation, usePatchEscalation } from '../../hooks';
import { ESCALATION_ILLEGAL_TRANSITION_CODE, VERSION_CONFLICT_CODE } from '../../model/types';
import type { ConversationDetail } from '../../model/types';
import { EpisodeTopicEditor } from './EpisodeTopicEditor';

export interface ConversationHeaderProps {
  detail: ConversationDetail;
  canManage: boolean;
  canViewCustomer: boolean;
  canViewPii: boolean;
  conflictActive: boolean;
  onVersionConflict: (sentVersion: number) => void;
  onActionSuccess: () => void;
  notify: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

/** The conversation header (design §7.1/§11.1) — name, customer link, sessions, episode line, actions. */
export function ConversationHeader({
  detail,
  canManage,
  canViewCustomer,
  canViewPii,
  conflictActive,
  onVersionConflict,
  onActionSuccess,
  notify,
}: ConversationHeaderProps) {
  const { t } = useTranslation('conversations');
  const locale = getIntlLocale();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const patchEscalation = usePatchEscalation(detail.id);
  const closeConversation = useCloseConversation(detail.id);

  const episode = detail.currentEpisode ?? null;
  const nameHidden = !detail.provisional && detail.displayName == null && !canViewPii;
  const displayName =
    detail.displayName ??
    (detail.provisional
      ? t('list.row.provisional', {
          channel: t(`channel.${detail.lastChannel}`, detail.lastChannel),
        })
      : t('detail.header.customerFallback'));

  function handleVersionedError(error: NormalizedApiError, sentVersion: number) {
    if (error.status === 409 && error.code === VERSION_CONFLICT_CODE) {
      onVersionConflict(sentVersion);
      return;
    }
    if (error.status === 409 && error.code === ESCALATION_ILLEGAL_TRANSITION_CODE) {
      notify('error', t('detail.escalation.updatedElsewhere'));
      onActionSuccess();
      return;
    }
    notify('error', error.message);
  }

  function acknowledge() {
    if (!detail.openEscalation || !episode) return;
    patchEscalation.mutate(
      {
        escalationId: detail.openEscalation.id,
        body: { status: 'ACKED', version: episode.version },
      },
      { onError: (error) => handleVersionedError(error, episode.version) },
    );
  }

  function handBack() {
    if (!detail.openEscalation || !episode) return;
    patchEscalation.mutate(
      {
        escalationId: detail.openEscalation.id,
        body: { status: 'RESOLVED', version: episode.version },
      },
      { onError: (error) => handleVersionedError(error, episode.version) },
    );
  }

  function confirmClose() {
    if (!episode) return;
    closeConversation.mutate(
      { version: episode.version },
      {
        onSuccess: () => setCloseDialogOpen(false),
        onError: (error) => {
          setCloseDialogOpen(false);
          handleVersionedError(error, episode.version);
        },
      },
    );
  }

  const canAcknowledge = canManage && detail.openEscalation?.status === 'REQUESTED';
  const canHandBack =
    canManage &&
    Boolean(detail.openEscalation) &&
    (episode?.status === 'WAITING_OPERATOR' || episode?.status === 'OPERATOR');
  const canClose = canManage && episode != null && episode.status !== 'CLOSED';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{ bgcolor: detail.provisional ? 'action.disabledBackground' : 'primary.main' }}
            >
              {initials(displayName)}
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography
                  variant="h4"
                  component="h1"
                  noWrap
                  sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                >
                  {displayName}
                </Typography>
                {nameHidden ? (
                  <Tooltip title={t('detail.header.nameHidden')}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<VisibilityOffRoundedIcon fontSize="inherit" />}
                      label={t('detail.header.nameHidden')}
                    />
                  </Tooltip>
                ) : null}
                {detail.customerId && canViewCustomer ? (
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/customers/${detail.customerId}`}
                  >
                    {t('detail.openInCustomers')}
                  </Button>
                ) : !detail.customerId ? (
                  <Tooltip title={t('detail.notLinkedTooltip')}>
                    <Chip size="small" variant="outlined" label={t('detail.notLinked')} />
                  </Tooltip>
                ) : null}
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {detail.sessions.map((session) => (
                  <Tooltip
                    key={session.id}
                    title={session.reachable ? undefined : t('composer.channelBlocked')}
                  >
                    <span>
                      <ChannelBadge
                        channel={session.channel}
                        blocked={!session.reachable}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                ))}
              </Stack>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            {canAcknowledge ? (
              <Tooltip title={conflictActive ? t('detail.conflict.reloadFirst') : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={conflictActive || patchEscalation.isPending}
                    onClick={acknowledge}
                    startIcon={
                      patchEscalation.isPending ? <CircularProgress size={14} /> : undefined
                    }
                  >
                    {t('detail.actions.acknowledge')}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canHandBack ? (
              <Tooltip
                title={
                  conflictActive
                    ? t('detail.conflict.reloadFirst')
                    : t('detail.actions.handBackTooltip')
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={conflictActive || patchEscalation.isPending}
                    onClick={handBack}
                  >
                    {t('detail.actions.handBack')}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canClose ? (
              <>
                <IconButton
                  size="small"
                  aria-label={t('detail.actions.more')}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  disabled={conflictActive}
                >
                  <MoreVertRoundedIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setCloseDialogOpen(true);
                    }}
                  >
                    {t('detail.actions.close')}
                  </MenuItem>
                </Menu>
              </>
            ) : null}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ConversationStatusChip status={episode?.status ?? detail.status} />
          {episode?.idle ? <IdleChip lastActivityAt={episode.lastActivityAt} /> : null}
          <Typography variant="body2" color="text.secondary">
            {t('detail.header.topic')}:
          </Typography>
          {episode ? (
            <EpisodeTopicEditor
              conversationId={detail.id}
              episodeId={episode.episodeId}
              topic={episode.topic}
              version={episode.version}
              canManage={canManage}
              disabled={conflictActive}
              onVersionConflict={onVersionConflict}
              onSaved={() => notify('success', t('episodes.topicSaved'))}
              notify={notify}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t('detail.header.noTopic')}
            </Typography>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {episode
            ? t('detail.header.episodeLine', {
                time: formatDayTime(episode.startedAt, locale),
                channel: t(`channel.${episode.openedByChannel}`, episode.openedByChannel),
                value: relativeTimeLabel(episode.lastActivityAt, locale),
              })
            : t('detail.header.noActiveEpisode')}
          {typeof detail.episodesCount === 'number' ? (
            <>
              {' · '}
              {t('detail.header.episodesCount', { count: detail.episodesCount })}
            </>
          ) : null}
        </Typography>
      </Stack>

      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
        <DialogTitle>{t('detail.closeDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('detail.closeDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setCloseDialogOpen(false)}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmClose}
            disabled={closeConversation.isPending}
            startIcon={
              closeConversation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ForumRoundedIcon />
              )
            }
          >
            {t('detail.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
